// Component Tokens
// Design tokens specific to UI components

import { colors, semanticColors } from './colors';
import { spacing, borderRadius, shadows, transitions } from './spacing';
import { typography } from './typography';

export const componentTokens = {
  // Button Component Tokens
  button: {
    // Primary Button
    primary: {
      background: colors.primary[500],
      backgroundHover: colors.primary[600],
      backgroundActive: colors.primary[700],
      backgroundDisabled: colors.gray[300],
      text: '#ffffff',
      textDisabled: colors.gray[500],
      border: 'transparent',
      borderHover: 'transparent',
      borderFocus: colors.primary[300],
      shadow: shadows.sm,
      shadowHover: shadows.md,
      borderRadius: borderRadius.lg,
      transition: `all ${transitions.duration.normal} ${transitions.easing.ease}`
    },

    // Secondary Button
    secondary: {
      background: semanticColors.surface.primary,
      backgroundHover: semanticColors.surface.hover,
      backgroundActive: semanticColors.surface.active,
      backgroundDisabled: semanticColors.surface.disabled,
      text: semanticColors.text.primary,
      textDisabled: semanticColors.text.disabled,
      border: semanticColors.border.primary,
      borderHover: semanticColors.border.hover,
      borderFocus: colors.primary[300],
      shadow: shadows.sm,
      shadowHover: shadows.md,
      borderRadius: borderRadius.lg,
      transition: `all ${transitions.duration.normal} ${transitions.easing.ease}`
    },

    // Danger Button
    danger: {
      background: colors.danger[500],
      backgroundHover: colors.danger[600],
      backgroundActive: colors.danger[700],
      backgroundDisabled: colors.gray[300],
      text: '#ffffff',
      textDisabled: colors.gray[500],
      border: 'transparent',
      borderHover: 'transparent',
      borderFocus: colors.danger[300],
      shadow: shadows.sm,
      shadowHover: shadows.md,
      borderRadius: borderRadius.lg,
      transition: `all ${transitions.duration.normal} ${transitions.easing.ease}`
    }
  },

  // Card Component Tokens
  card: {
    background: semanticColors.surface.primary,
    backgroundHover: semanticColors.surface.primary,
    border: semanticColors.border.primary,
    borderHover: semanticColors.border.hover,
    borderRadius: borderRadius.xl,
    shadow: shadows.sm,
    shadowHover: shadows.md,
    padding: spacing[6],
    transition: `all ${transitions.duration.normal} ${transitions.easing.ease}`
  },

  // Status Badge Component Tokens
  statusBadge: {
    running: {
      background: colors.success[100],
      text: colors.success[800],
      border: colors.success[200],
      indicator: colors.success[500]
    },
    stopped: {
      background: colors.gray[100],
      text: colors.gray[700],
      border: colors.gray[200],
      indicator: colors.gray[500]
    },
    starting: {
      background: colors.warning[100],
      text: colors.warning[800],
      border: colors.warning[200],
      indicator: colors.warning[500]
    },
    error: {
      background: colors.danger[100],
      text: colors.danger[800],
      border: colors.danger[200],
      indicator: colors.danger[500]
    }
  },

  // Form Component Tokens
  form: {
    input: {
      background: semanticColors.surface.primary,
      backgroundDisabled: semanticColors.surface.disabled,
      backgroundFocus: semanticColors.surface.primary,
      border: semanticColors.border.primary,
      borderHover: semanticColors.border.hover,
      borderFocus: colors.primary[500],
      borderError: colors.danger[500],
      borderRadius: borderRadius.lg,
      text: semanticColors.text.primary,
      textDisabled: semanticColors.text.disabled,
      placeholder: semanticColors.text.tertiary,
      shadow: shadows.sm,
      shadowFocus: `0 0 0 3px ${colors.primary[100]}`,
      padding: `${spacing[3]} ${spacing[4]}`,
      fontSize: typography.fontSize.base,
      transition: `all ${transitions.duration.fast} ${transitions.easing.ease}`
    },

    label: {
      text: semanticColors.text.primary,
      textRequired: colors.danger[600],
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      marginBottom: spacing[2]
    },

    errorMessage: {
      text: colors.danger[600],
      fontSize: typography.fontSize.sm,
      marginTop: spacing[1]
    }
  },

  // Modal Component Tokens
  modal: {
    overlay: {
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      zIndex: 1400
    },
    content: {
      background: semanticColors.surface.primary,
      border: semanticColors.border.primary,
      borderRadius: borderRadius['2xl'],
      shadow: shadows['2xl'],
      maxWidth: '500px',
      padding: spacing[8]
    },
    header: {
      borderBottom: semanticColors.border.primary,
      paddingBottom: spacing[4],
      marginBottom: spacing[6]
    }
  },

  // Navigation Component Tokens
  navigation: {
    sidebar: {
      background: semanticColors.surface.primary,
      border: semanticColors.border.primary,
      width: '280px',
      shadow: shadows.lg
    },
    
    item: {
      background: 'transparent',
      backgroundHover: semanticColors.surface.hover,
      backgroundActive: colors.primary[50],
      text: semanticColors.text.secondary,
      textHover: semanticColors.text.primary,
      textActive: colors.primary[700],
      borderRadius: borderRadius.lg,
      padding: `${spacing[3]} ${spacing[4]}`,
      marginBottom: spacing[1],
      transition: `all ${transitions.duration.fast} ${transitions.easing.ease}`
    },

    section: {
      headerText: semanticColors.text.tertiary,
      headerFontSize: typography.fontSize.xs,
      headerFontWeight: typography.fontWeight.semibold,
      headerLetterSpacing: typography.letterSpacing.wide,
      headerMarginBottom: spacing[2],
      marginBottom: spacing[6]
    }
  },

  // Metric Card Component Tokens
  metricCard: {
    background: semanticColors.surface.primary,
    border: semanticColors.border.primary,
    borderRadius: borderRadius.xl,
    shadow: shadows.sm,
    shadowHover: shadows.md,
    padding: spacing[6],
    transition: `all ${transitions.duration.normal} ${transitions.easing.ease}`,
    
    header: {
      text: semanticColors.text.secondary,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      marginBottom: spacing[4]
    },
    
    value: {
      text: semanticColors.text.primary,
      fontSize: typography.fontSize['3xl'],
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight
    },
    
    trend: {
      positive: colors.success[600],
      negative: colors.danger[600],
      neutral: semanticColors.text.tertiary,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium
    }
  },

  // Alert Component Tokens
  alert: {
    success: {
      background: colors.success[50],
      border: colors.success[200],
      text: colors.success[800],
      icon: colors.success[600]
    },
    warning: {
      background: colors.warning[50],
      border: colors.warning[200],
      text: colors.warning[800],
      icon: colors.warning[600]
    },
    error: {
      background: colors.danger[50],
      border: colors.danger[200],
      text: colors.danger[800],
      icon: colors.danger[600]
    },
    info: {
      background: colors.primary[50],
      border: colors.primary[200],
      text: colors.primary[800],
      icon: colors.primary[600]
    }
  },

  // Loading Component Tokens
  loading: {
    spinner: {
      color: colors.primary[500],
      size: {
        sm: '16px',
        md: '24px',
        lg: '32px'
      }
    },
    skeleton: {
      background: colors.gray[200],
      shimmer: colors.gray[300],
      borderRadius: borderRadius.md
    }
  }
} as const;

// Component size variants
export const componentSizes = {
  button: {
    sm: {
      fontSize: typography.fontSize.sm,
      padding: `${spacing[2]} ${spacing[3]}`,
      height: '36px'
    },
    md: {
      fontSize: typography.fontSize.base,
      padding: `${spacing[3]} ${spacing[4]}`,
      height: '44px'
    },
    lg: {
      fontSize: typography.fontSize.lg,
      padding: `${spacing[4]} ${spacing[6]}`,
      height: '52px'
    }
  },

  input: {
    sm: {
      fontSize: typography.fontSize.sm,
      padding: `${spacing[2]} ${spacing[3]}`,
      height: '36px'
    },
    md: {
      fontSize: typography.fontSize.base,
      padding: `${spacing[3]} ${spacing[4]}`,
      height: '44px'
    },
    lg: {
      fontSize: typography.fontSize.lg,
      padding: `${spacing[4]} ${spacing[5]}`,
      height: '52px'
    }
  }
} as const;